import React from 'react'
import { css } from '@emotion/core'
import { Button, Modal } from '@dw/helix'
import { Field, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import CustomFormikInputField from './CustomFormikInputField'
import { SERIAL_NUMBER_PATTERN } from '../utils/serialNumbers'

export interface ModalFormData {
  articleId: string
  serialNumbers: string[]
}

interface ManualInputModalComponentProps {
  onSubmit: (formData: ModalFormData) => void
  onClose: () => void
  serialNumbers: string[]
  articleId: string;
}

const manualInputModalStyles = css`
  margin-top: 116px;
`

const manualInputModalFormStyles = css`
  max-width: 544px;
  margin-right: auto;
  margin-left: auto;
`

const manualInputModalInputStyles = css`
  margin-bottom: 20px;
`

const buttonContainerStyles = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
`

const buttonStyles = css`
  display: block;
  width: calc(50% - 10px);
  &:first-of-type {
    margin-right: 20px;
  }
`

const stopPropagation = (event: React.FormEvent<HTMLFormElement | HTMLInputElement>, callback: () => void) => {
  event.stopPropagation()
  callback()
}

const ManualInputModal = ({ onSubmit, onClose, serialNumbers, articleId }: ManualInputModalComponentProps) => {
  const manualInputFormInitialValues = {
    articleId,
    serialNumber: '',
  }

  const manualInputFormValidationSchema = Yup.object().shape({
    articleId: Yup.string().required("Article Number can't be empty"),
    serialNumber: Yup.string()
      .required("Serial Number can't be empty")
      .matches(SERIAL_NUMBER_PATTERN, 'Serial Number is not valid')
      .test(
        'is-duplicate',
        'Serial Number has already been added in session',
        serialNumber => !serialNumbers.includes(serialNumber),
      ),
  })

  return (
    <Modal title="Manual Input" onClose={onClose} css={manualInputModalStyles}>
      {({ close }) => (
        <Formik
          initialValues={manualInputFormInitialValues}
          validationSchema={manualInputFormValidationSchema}
          enableReinitialize={true}
          onSubmit={values => {
            onSubmit({ articleId: values.articleId, serialNumbers: [values.serialNumber] })
            close()
          }}
        >
          {({
            handleSubmit,
            values,
            errors,
            setFieldValue,
            isValid,
          }: FormikProps<{ articleId: string; serialNumber: string }>) => {
            return (
              <form
                onSubmit={event => stopPropagation(event, () => handleSubmit(event))}
                css={manualInputModalFormStyles}
              >
                <div css={manualInputModalInputStyles}>
                  <Field
                    id="manual-input-article-number"
                    name="articleId"
                    label="ARTICLE NUMBER"
                    theme="light"
                    value={values.articleId}
                    disabled={false}
                    status={errors.articleId ? 'error' : 'normal'}
                    onChange={(event: React.FormEvent<HTMLInputElement>) => {
                      const articleIdInputElement = event.target as HTMLInputElement
                      stopPropagation(event, () => setFieldValue('articleId', articleIdInputElement.value))
                    }}
                    component={CustomFormikInputField}
                  />
                </div>
                <div css={manualInputModalInputStyles}>
                  <Field
                    id="manual-input-serial-number"
                    name="serialNumber"
                    label="SERIAL NUMBER"
                    theme="light"
                    value={values.serialNumber}
                    disabled={false}
                    status={errors.serialNumber ? 'error' : 'normal'}
                    onChange={(event: React.FormEvent<HTMLInputElement>) => {
                      const serialNumberInputElement = event.target as HTMLInputElement
                      stopPropagation(event, () => setFieldValue('serialNumber', serialNumberInputElement.value))
                    }}
                    component={CustomFormikInputField}
                  />
                </div>
                <div css={buttonContainerStyles}>
                  <Button type="button" onClick={close} theme="secondary" css={buttonStyles}>
                    GO BACK
                  </Button>
                  <Button type="submit" disabled={!isValid} css={buttonStyles}>
                    SUBMIT
                  </Button>
                </div>
              </form>
            )
          }}
        </Formik>
      )}
    </Modal>
  )
}

export default ManualInputModal
